import { useEffect, useId, useState, type ReactNode } from 'react'
import { anmeldung, branchen, funktionen, konferenz } from '@/data/inhalte'
import { leseHerkunft, sendeAnmeldung, type Anmeldung, type SubmitErgebnis } from '@/lib/api'
import { leereWerte, pruefe, type FormWerte } from '@/lib/pruefung'

type Zustand = 'ruhe' | 'sendet' | 'fertig'
type Textfeld = 'vorname' | 'nachname' | 'email' | 'organisation'

export interface FormTexte {
  /** Beschriftung des Formulars fuer Screenreader */
  label: string
  knopf: string
  danke: string
  dankeText: string
}

const STANDARD_TEXTE: FormTexte = {
  label: 'Zum FOM Live-Talk anmelden',
  knopf: anmeldung.knopf,
  danke: anmeldung.danke,
  dankeText: anmeldung.dankeText,
}

interface Props {
  senden?: (anmeldung: Anmeldung) => Promise<SubmitErgebnis>
  /** meldet jede Eingabe (fuer den Ausweis) */
  beiAenderung?: (werte: FormWerte) => void
  beiErfolg?: () => void
  /** Vorbelegung, z. B. aus der persoenlichen Einladung (Seite /vor-ort) */
  startwerte?: Partial<FormWerte>
  /** ersetzt den Hinweis „Online-Event …“ oben im Formular */
  hinweis?: ReactNode
  /** Symbol vor dem Hinweis (SVG-Inhalt, 24er-Raster); Standard: Bildschirm fuer „Online-Event“ */
  hinweisSymbol?: ReactNode
  texte?: FormTexte
  /** ohne Auswahl „Branche“ (Seite /vor-ort) */
  ohneBranche?: boolean
}

export default function AnmeldeForm({ senden = sendeAnmeldung, beiAenderung, beiErfolg, startwerte, hinweis, texte = STANDARD_TEXTE, ohneBranche = false, hinweisSymbol }: Readonly<Props>) {
  const [werte, setWerte] = useState<FormWerte>(() => ({ ...leereWerte, ...startwerte }))
  const [fehler, setFehler] = useState<Record<string, string>>({})
  const [zustand, setZustand] = useState<Zustand>('ruhe')
  const [meldung, setMeldung] = useState<string | null>(null)
  const [honigtopf, setHonigtopf] = useState('')
  const id = useId()

  useEffect(() => { beiAenderung?.(werte) }, [werte, beiAenderung])

  const aendern = (feld: keyof FormWerte, wert: string | boolean) => {
    setWerte(vorher => ({ ...vorher, [feld]: wert }))
    setFehler(({ [feld]: _weg, ...rest }) => rest)
  }

  const absenden = async (e: React.FormEvent) => {
    e.preventDefault()
    if (zustand !== 'ruhe') return
    const gefunden = pruefe(werte)
    if (Object.keys(gefunden).length > 0) {
      setFehler(gefunden)
      document.getElementById(`${id}-${Object.keys(gefunden)[0]}`)?.focus()
      return
    }
    // Honigtopf: nur automatisierte Zugriffe fuellen dieses Feld
    if (honigtopf.trim()) { setZustand('fertig'); return }

    setZustand('sendet')
    setMeldung(null)
    const ergebnis = await senden({
      vorname: werte.vorname.trim(),
      nachname: werte.nachname.trim(),
      email: werte.email.trim(),
      organisation: werte.organisation.trim() || undefined,
      branche: werte.branche || undefined,
      funktion: werte.funktion || undefined,
      einwilligung: true,
      herkunft: leseHerkunft(window.location.search, window.location.href),
    })
    if (ergebnis.ok) { setZustand('fertig'); beiErfolg?.(); return }
    setZustand('ruhe')
    setMeldung(ergebnis.meldung)
  }

  if (zustand === 'fertig') {
    return (
      <div role="status" className="rounded-[32px] bg-white p-8 text-tinte shadow-[0_30px_70px_rgba(0,19,28,.25)] sm:p-10">
        <div className="mb-5 grid h-12 w-12 place-items-center rounded-full bg-konf-hell/20 text-xl text-konf-teal">✓</div>
        <h3 className="text-[28px] text-konf-nacht">{texte.danke}</h3>
        <p className="mt-3 text-tinte-leise">{texte.dankeText}</p>
      </div>
    )
  }

  const eingabe = 'h-12 w-full rounded-xl border bg-[#EEF3F3] px-4 text-base transition hover:bg-[#E7EEEE] '
    + 'focus:border-konf-teal focus:bg-white focus:outline-none focus:ring-[3px] focus:ring-konf-teal/20 '

  const feld = (name: Textfeld, beschriftung: string, typ: 'text' | 'email', pflicht: boolean, autoComplete: string) => (
    <div>
      <label htmlFor={`${id}-${name}`} className="mb-2 block text-[14px] font-medium text-tinte-leise">
        {beschriftung}
        {!pflicht && <span className="ml-1 font-normal">(optional)</span>}
      </label>
      <input
        id={`${id}-${name}`}
        name={name}
        type={typ}
        required={pflicht}
        autoComplete={autoComplete}
        value={werte[name]}
        onChange={e => aendern(name, e.target.value)}
        aria-invalid={fehler[name] ? true : undefined}
        aria-describedby={fehler[name] ? `${id}-${name}-fehler` : undefined}
        className={eingabe + (fehler[name] ? 'border-[#C0332B]' : 'border-transparent')}
      />
      {fehler[name] && <p id={`${id}-${name}-fehler`} className="mt-1.5 text-[13px] text-[#C0332B]">{fehler[name]}</p>}
    </div>
  )

  const auswahl = (name: 'branche' | 'funktion', beschriftung: string, optionen: string[]) => (
    <div>
      <label htmlFor={`${id}-${name}`} className="mb-2 block text-[14px] font-medium text-tinte-leise">
        {beschriftung}
        <span className="ml-1 font-normal">(optional)</span>
      </label>
      <select
        id={`${id}-${name}`}
        name={name}
        value={werte[name]}
        onChange={e => aendern(name, e.target.value)}
        className={eingabe + 'border-transparent pr-10 ' + (werte[name] ? 'text-tinte' : 'text-tinte-leise')}
      >
        <option value="">Bitte wählen</option>
        {optionen.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  )

  return (
    <form
      onSubmit={absenden}
      noValidate
      aria-label={texte.label}
      className="relative rounded-[32px] bg-white p-7 text-tinte shadow-[0_30px_70px_rgba(0,19,28,.25)] sm:p-10"
    >
      <p className="mb-7 flex items-start gap-3 rounded-2xl bg-konf-hell/15 px-4 py-3.5 text-[14.5px] leading-snug text-konf-nacht">
        <svg aria-hidden="true" viewBox="0 0 24 24" className="mt-0.5 h-5 w-5 shrink-0 text-konf-teal" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          {hinweisSymbol ?? <><rect x="2.5" y="4" width="19" height="13" rx="2" /><path d="M8 21h8M12 17v4M10 8.5l4.5 2.5L10 13.5Z" /></>}
        </svg>
        <span>{hinweis ?? <><strong className="font-display font-bold">{konferenz.format}:</strong> {konferenz.formatHinweis}</>}</span>
      </p>
      <div className="grid gap-5">
        <div className="grid gap-5 sm:grid-cols-2">
          {feld('vorname', 'Vorname', 'text', true, 'given-name')}
          {feld('nachname', 'Nachname', 'text', true, 'family-name')}
        </div>
        {feld('email', 'E-Mail-Adresse', 'email', true, 'email')}
        {feld('organisation', 'Unternehmen / Verband', 'text', false, 'organization')}
        {/* untereinander - nebeneinander werden lange Einträge abgeschnitten */}
        {!ohneBranche && auswahl('branche', 'Branche', branchen)}
        {auswahl('funktion', 'Funktion', funktionen)}

        <div aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 overflow-hidden">
          <label htmlFor={`${id}-webseite`}>Webseite</label>
          <input id={`${id}-webseite`} name="webseite" tabIndex={-1} autoComplete="off" value={honigtopf} onChange={e => setHonigtopf(e.target.value)} />
        </div>

        <div>
          <label htmlFor={`${id}-einwilligung`} className="flex cursor-pointer items-start gap-3 py-1 text-[13.5px] leading-relaxed">
            <input
              id={`${id}-einwilligung`}
              type="checkbox"
              checked={werte.einwilligung}
              onChange={e => aendern('einwilligung', e.target.checked)}
              aria-invalid={fehler.einwilligung ? true : undefined}
              aria-describedby={fehler.einwilligung ? `${id}-einwilligung-fehler` : undefined}
              className="mt-0.5 h-6 w-6 shrink-0 cursor-pointer accent-konf-teal"
            />
            <span>
              {/* PLATZHALTER: Einwilligungstext und Verweis kommen aus dem Haus (Datenschutz). */}
              Ich bin einverstanden, dass die FOM meine Angaben zur Durchführung des Live-Talks verarbeitet und mich dazu
              kontaktiert. <a className="text-konf-teal underline underline-offset-2" href="#datenschutz">Datenschutzhinweise</a>
            </span>
          </label>
          {fehler.einwilligung && <p id={`${id}-einwilligung-fehler`} className="mt-1.5 text-[13px] text-[#C0332B]">{fehler.einwilligung}</p>}
        </div>
      </div>

      {meldung && <p role="alert" className="mt-5 rounded-xl bg-[#FDECEC] p-3 text-[13.5px] text-[#C0332B]">{meldung}</p>}

      <button type="submit" disabled={zustand === 'sendet'} className="knopf knopf-dunkel mt-7 w-full disabled:opacity-70 sm:w-auto">
        {zustand === 'sendet' ? 'Wird übermittelt …' : texte.knopf}
      </button>
    </form>
  )
}
